"use client";

import { useState, useCallback, useMemo, useRef } from "react";
import { useAccount, usePublicClient, useWalletClient, useChainId } from "wagmi";
import { useWriteContract, useWaitForTransactionReceipt } from "wagmi";
import manifest from "../contracts-manifest.json";
import type { Abi } from "viem";
import { parseEventLogs, decodeEventLog, type Log } from "viem";
import { previewWrite } from "../lib/previewWrite";

// ---------------------------------------------------------------------------
// Types – shared with Solidity
// ---------------------------------------------------------------------------
export type VolumeType = 0 | 1 | 2 | 3 | 4; // SALE_LISTING=0, AUCTION_ENGLISH=1, AUCTION_DUTCH=2, OFFER=3, ERC1155=4
// FeeModel from LibDiamond: SPLIT = both sides pay half; SELLER = seller pays 100%, buyer pays 0 on top.
export type FeeModel = 0 | 1;

// Facet names
type FacetName = "Core" | "English" | "Dutch" | "ERC1155" | "Offers" | "Diamond" | "Sales";

// ---------------------------------------------------------------------------
// ABI loader – reads from manifest (generated from out/*.json)
// ---------------------------------------------------------------------------
function loadFacetAbi(name: string): any[] {
  // Try exact match first
  let entry = (manifest.contracts as any[]).find((c) => c.name === name);
  if (entry) return entry?.abi ?? [];
  // Try with AuctionManager prefix (as manifest entries are like AuctionManagerEnglish)
  entry = (manifest.contracts as any[]).find((c) => c.name === `AuctionManager${name}`);
  if (entry) return entry?.abi ?? [];
  // Fallback: try lowercase? Not needed.
  return [];
}

// Union of every AuctionManager facet ABI in the manifest. Needed because
// `newListing` is emitted by several facets (Sales, English, ERC1155) while a
// given write is decoded against ONE facet's ABI — decoding against only that
// facet yields [] for the other emitters. Events only, so parseEventLogs can
// never mistake a duplicate function signature for an event.
const ALL_EVENT_ABIS: any[] = (() => {
  const out: any[] = [];
  const seen = new Set<string>();
  for (const c of (manifest.contracts as any[])) {
    if (!String(c?.name || "").startsWith("AuctionManager")) continue;
    for (const e of c?.abi ?? []) {
      if (e?.type !== "event") continue;
      const key = `${e.name}(${(e.inputs || []).map((i: any) => `${i.type}${i.indexed ? " indexed" : ""}`).join(",")})`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(e);
    }
  }
  return out;
})();

/**
 * Decode logs by event name against a combined ABI. Unlike decodeEventLogs
 * (which swallows every error and returns []), this surfaces failures so a
 * mismatch is visible instead of silently degrading.
 */
function decodeLogs(logs: Log[] | undefined, abi: any, eventName: string): any[] {
  if (!logs?.length || !abi?.length) return [];
  try {
    return parseEventLogs({ logs: logs as Log[], abi: abi as Abi, eventName } as any);
  } catch (e: any) {
    console.warn(`[auction] decodeLogs(${eventName}) failed:`, e?.message || e);
    return [];
  }
}

/**
 * Decode EVERY AuctionManager event in a receipt, regardless of which write
 * produced it. Each entry is `{ eventName, args, address, logIndex }`.
 *
 * Why this exists: each action emits a different event —
 *   removeListingBeforeEndTime -> removedListing
 *   cancelSaleListing           -> listingCancelled
 *   acceptSaleListing           -> auctionWinner
 *   finalizeEarly / buy-now     -> auctionWinner | auctionNoWinner
 *   Dutch settle                -> dutchAuctionEnd
 * — and none of them is `newListing`. Decoding only `newListing` (as the
 * listing path does) means every non-listing action has nothing to hand the
 * backend, which is exactly what the close/finalize endpoint needs.
 *
 * Undecodable logs are reported as `{ eventName: '<undecoded>' }` rather than
 * dropped, so a signature we don't recognise is visible instead of silently
 * missing.
 */
function decodeAllAuctionEvents(logs: Log[] | undefined): any[] {
  if (!logs?.length) return [];
  const out: any[] = [];
  for (const log of logs as Log[]) {
    const entry: any = {
      address: log.address,
      logIndex: log.logIndex,
      eventName: "<undecoded>",
      args: null,
    };
    try {
      // decodeEventLog, not parseEventLogs: viem's parseEventLogs REQUIRES an
      // eventName filter (passing undefined returns []), which would silently
      // decode nothing here. decodeEventLog takes a single log and throws on
      // an unknown topic0 — caught per-log below so one Transfer from the NFT
      // contract doesn't abort the whole receipt.
      const decoded: any = decodeEventLog({ abi: ALL_EVENT_ABIS as any, data: log.data, topics: log.topics } as any);
      if (decoded) {
        entry.eventName = decoded.eventName;
        entry.args = decoded.args;
      }
    } catch {
      /* leave as <undecoded> */
    }
    out.push(entry);
  }
  return out;
}

/** Pick the first decoded event with this name, or null. */
function findEvent(events: any[], name: string): any | null {
  return events.find((e) => e?.eventName === name) ?? null;
}

// ---------------------------------------------------------------------------
// Function routing table – maps function name → owning facet
// ---------------------------------------------------------------------------
const FN_ROUTES: Record<string, FacetName> = {
  // Core — ownership/init, fee config, treasury & owed-refund accounting
  initialize: "Core",
  owner: "Core",
  paused: "Core",
  transferOwnership: "Core",
  renounceOwnership: "Core",
  setGasAccount: "Core",
  setAdminAddress: "Core",
  showFeesAccrued: "Core",
  configureFeePercentages: "Core",
  setContractFeeConfig: "Core",
  clearContractFeeConfig: "Core",
  getContractFeeConfig: "Core",
  getEffectiveFeeConfig: "Core",
  getHighBid: "Core",
  getRemovalFee: "Core",
  getEarlyFinalizeFee: "Core",
  getGlobalFeeConfig: "Core",
  getContractFeeSettings: "Core",
  getFeeConfig: "Core",
  refundsOwedTotal: "Core",
  treasuryTotalOwed: "Core",
  withdrawTreasury: "Core",

  // English auction + sale listings + analytics
  createAuction: "English",
  createDutchAuction: "English",
  createSaleListing: "Sales",
  submitNewBid: "English",
  finalizeAuction: "English",
  finalizeEarly: "English",
  acceptSaleListing: "Sales",
  cancelSaleListing: "Sales",
  removeListingBeforeEndTime: "English",
  getActiveAuction: "English",
    getActiveAuctionIDs: "English",
    getActiveSaleListingIDs: "English",
    getSaleListingInfo: "English",
    getAuctionFinancials: "English",
  getAuctionMeta: "English",
  getAuctionStatus: "English",
  getBidCount: "English",
  getCICount: "English",
  getCICountOnly: "English",
  allAuctions_toks: "English",
  getTokenSaleHistory: "English",
  getOwnerOf: "English",
  minNextBidAmount: "English",
  getFloorPrice: "English",
  getTotalVolume: "English",
  getSaleCount: "English",
  getVolumeByType: "English",
  getCountByType: "English",
  getGlobalTotalVolume: "English",
  getGlobalSaleCount: "English",

  // Dutch auction
  acceptDutchAuctionPrice: "Dutch",
  getCurrentDutchPrice: "Dutch",

  // ERC-1155
  createERC1155SaleListing: "ERC1155",
  createERC1155Auction: "ERC1155",
  createERC1155DutchAuction: "ERC1155",
  batchCreateERC1155Auction: "ERC1155",
  acceptERC1155SaleListing: "ERC1155",
  acceptERC1155AuctionPrice: "ERC1155",
  acceptERC1155DutchAuctionPrice: "ERC1155",
  finalizeERC1155Auction: "ERC1155",
  finalizeERC1155Early: "ERC1155",
  getERC1155SaleListing: "ERC1155",
  getERC1155AuctionStatus: "ERC1155",
  getERC1155CurrentDutchPrice: "ERC1155",
  getERC1155Volume: "ERC1155",
  minNextBidAmountERC1155: "ERC1155",

  // Offers facet — offer lifecycle + the single shared refund entry points.
  // claimRefund is the ONLY copy left in the diamond: it was previously
  // duplicated verbatim in English, Dutch, Sales, Offers and ERC1155, and a
  // diamond routes one selector to one facet, so five copies cannot be cut.
  // AuctionManagerDiamond also declares it, but the live proxy is a plain OZ
  // ERC1967Proxy with a fixed implementation, so the diamond's own runtime is
  // never reached and the Offers copy is the one actually served. Both read
  // the same shared ds.refunds map, so this pays refunds from any auction type.
  makeOffer: "Offers",
  makeERC1155Offer: "Offers",
  acceptOffer: "Offers",
  cancelOffer: "Offers",
  finalizeOffer: "Offers",
  claimRefund: "Offers",
  refundBalanceOf: "Offers",

  // Diamond management
  diamondCut: "Diamond",
  diamondCutLength: "Diamond",
  getFacets: "Diamond",
  getFunctionSelector: "Diamond",
  facet: "Diamond",
  upgradeTo: "Diamond",
  upgradeToAndCall: "Diamond",
  supportsInterface: "Diamond",
  onERC1155Received: "Diamond",
  onERC1155BatchReceived: "Diamond",
};

// ---------------------------------------------------------------------------
// Diamond proxy address — resolved per connected chain from the manifest's
// chainAddrMap, which is generated from THIS project's src/chainData.json
// (see documentation/ABI_MANIFEST.md). No Anvil default: a chain that has no
// deployed AuctionManager must fail loudly rather than silently talk to a
// stale local address.
// ---------------------------------------------------------------------------
const STORAGE_SLOT = "0xb1f3e28388b163d5849928840d81f73e350e570a3026cc553ee67b1c889be0ab";

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export function useAuctionManager(
  overrideProxy?: string,
  opts?: { previewWrites?: boolean; chainId?: number }
) {
  const { address: userAddress, isConnected } = useAccount();
  const previewWrites = opts?.previewWrites ?? true;
  // Reads default to the connected chain, but a caller can target another
  // chain (e.g. a collection on Anvil while the wallet sits on Coston).
  // Writes ALWAYS go through the connected wallet, so they stay on connectedChainId.
  const connectedChainId = useChainId();
  const readChainId = opts?.chainId ?? connectedChainId;
  const publicClient = usePublicClient({ chainId: readChainId });
  // Writes must simulate + broadcast on the CONNECTED chain, never on a
  // read chain the wallet isn't attached to. Keep them separate.
  const walletChainClient = usePublicClient({ chainId: connectedChainId });
  const { data: walletClient } = useWalletClient();
  const chainId = connectedChainId;

  // Per-chain proxy from the manifest (chainAddrMap), then an explicit caller
  // override. Resolved for the READ chain so a caller can read a collection
  // that lives on a different chain than the connected wallet.
  const chainProxyMap = (manifest as any).chainAddrMap || {};
  const chainProxy = chainProxyMap[String(readChainId)] as string | undefined;
  const proxyAddress = (chainProxy || overrideProxy) as `0x${string}` | undefined;
  const hasProxy = Boolean(proxyAddress);

  // The AuctionManager proxy on the CONNECTED chain — what approvals and all
  // writes must target. Distinct from proxyAddress (which may be a read-chain
  // override).
  const connectedProxyAddress = (
    chainProxyMap[String(connectedChainId)] || overrideProxy
  ) as `0x${string}` | undefined;

  const { writeContractAsync, data: txHash } = useWriteContract();

  // Waiting for tx receipt (TanStack Query v5 renamed isLoading -> isPending)
  const { isPending: isTxLoading } = useWaitForTransactionReceipt({ hash: txHash });

  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);
  const [lastTx, setLastTx] = useState<`0x${string}` | null>(null);
  const [createdAuctionId, setCreatedAuctionId] = useState<string | null>(null);
  const [lastReceipt, setLastReceipt] = useState<any>(null);

  // Generic event log decoder — callers supply eventName + ABI
  const decodeEventLogs = useCallback(
    (logs: Log[], abi: Abi, eventName: string): any[] => {
      if (!logs || !logs.length || !abi) return [];
      try {
        return parseEventLogs({ logs: logs as Log[], abi, eventName } as any);
      } catch (e: any) {
        // ABI may not contain this event
        return [];
      }
    },
    []
  );

  // Memoized ABI lookups
  const allAbis = useMemo(() => {
    const routes = new Set(Object.values(FN_ROUTES));
    const abis: Record<string, any[]> = {};
    for (const name of routes) {
      abis[name] = loadFacetAbi(name);
    }
    return abis;
  }, []);

  // -----------------------------------------------------------------------
  // Generic read via proxy
  // All read calls go through the Diamond proxy address
  // -----------------------------------------------------------------------
  const callRead = useCallback(
    async (fnName: string, args: any[] = []): Promise<any> => {
      if (!publicClient) throw new Error("No public client");
      // Chains with no marketplace deployment (chainAddrMap has no entry and no
      // caller override) cannot serve reads — fail with a clear message
      // instead of letting viem encode against an undefined address.
      if (!proxyAddress) {
        const msg = `No marketplace contract deployed on chain ${readChainId}`;
        setError(msg);
        throw new Error(msg);
      }

      const facet = FN_ROUTES[fnName];
      const abi = allAbis[facet];
      if (!abi || !abi.length) throw new Error(`No ABI for ${facet}`);

      setLoading(true);
      setError(null);
      try {
        // `account` matters for reads too: several getters are `onlyOwner`
        // (showFeesAccrued), which checks _msgSender(). An eth_call without
        // `from` defaults to the zero address, so those revert "Ownable: caller
        // is not the owner" even with the owner wallet connected. Harmless for
        // public getters, so pass the connected address whenever we have one.
        const data = await (publicClient as any).readContract({
          address: proxyAddress as `0x${string}`,
          abi: abi as Abi,
          functionName: fnName,
          args,
          ...(userAddress ? { account: userAddress } : {}),
        } as any);
        setResult(data);
        return data;
      } catch (e: any) {
        setError(e?.message || String(e));
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [publicClient, allAbis, proxyAddress, userAddress]
  );

  // -----------------------------------------------------------------------
  // Generic write via connected wallet
  // -----------------------------------------------------------------------
  const callWrite = useCallback(
    async (fnName: string, args: any[] = [], value?: bigint): Promise<`0x${string}` | null> => {
      if (!walletClient || !publicClient || !userAddress)
        throw new Error("Wallet not connected");
      // Writes resolve their own proxy from the CONNECTED chain — a read chain
      // override must never redirect a transaction.
      const writeProxy = connectedProxyAddress;
      if (!writeProxy) {
        const msg = `No marketplace contract deployed on chain ${chainId}`;
        setError(msg);
        throw new Error(msg);
      }

      const facet = FN_ROUTES[fnName];
      const abi = allAbis[facet];
      if (!abi || !abi.length) throw new Error(`No ABI for ${facet}`);

      setLoading(true);
      setError(null);
      try {
        // Simulate and await the receipt on the wallet's own chain.
        const writeClient: any = walletChainClient || publicClient;
        const res = await previewWrite(writeClient, { write: writeContractAsync } as any, {
          address: writeProxy as `0x${string}`,
          abi: abi as Abi,
          functionName: fnName,
          args,
          account: userAddress,
          ...(value !== undefined ? { value } : {}),
        } as any, { enabled: previewWrites })
        if (!res.ok) { setError(res.error || 'Preview failed'); return null }
        if (!res.hash) { setError(res.error || 'No transaction hash returned'); return null }
        const hash = res.hash as `0x${string}`;
        setLastTx(hash);
        // Wait for receipt; decode events via generic decoder (newListing etc.)
        const receipt = await writeClient.waitForTransactionReceipt({ hash });
        let decodedEventId = null;
        try {
          const evs = decodeEventLogs(receipt.logs as Log[], abi as Abi, "newListing");
          if (evs && evs.length > 0) {
            const a = (evs[0] as any).args;
            decodedEventId = a?.auctionId ? String(a.auctionId) : String(a?.auctionID || null);
          }
        } catch (_) {}
        setCreatedAuctionId(decodedEventId);
        setLastReceipt({
          hash, blockNumber: receipt.blockNumber?.toString() || null,
          gasUsed: receipt.gasUsed?.toString() || null,
          status: receipt.status || null,
          logs: receipt.logs.map((l: any) => ({ address: l.address, data: l.data, topics: l.topics })),
          decodedEventId,
          decodedEvents: (receipt.logs ? decodeEventLogs(receipt.logs as Log[], abi as Abi, "newListing") : []),
        });
        return hash;
      } catch (e: any) {
        setError(e?.message || String(e));
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [walletClient, publicClient, walletChainClient, userAddress, writeContractAsync, allAbis, chainProxyMap, chainId, overrideProxy]
  );

  // Same as `callWrite`, but ALSO returns the already-mined receipt and the
  // decoded newListing id. `callWrite` awaits the receipt internally, so a
  // caller that re-waits on its own client can end up polling a DIFFERENT chain
  // than the one that mined the tx (the write always lands on the connected
  // chain). That mismatch never rejects — it just hangs forever — while the
  // receipt was sitting in memory the whole time. Expose it so callers never
  // need a second wait.
  const callWriteWithReceipt = useCallback(
    async (fnName: string, args: any[] = [], value?: bigint) => {
      if (!walletClient || !publicClient || !userAddress)
        throw new Error("Wallet not connected");
      const writeProxy = connectedProxyAddress;
      if (!writeProxy) {
        const msg = `No marketplace contract deployed on chain ${chainId}`;
        setError(msg);
        throw new Error(msg);
      }
      const facet = FN_ROUTES[fnName];
      const abi = allAbis[facet];
      if (!abi || !abi.length) throw new Error(`No ABI for ${facet}`);

      setLoading(true);
      setError(null);
      try {
        // MUST be the connected chain's client — the hash lives there.
        const writeClient: any = walletChainClient || publicClient;
        const res = await previewWrite(writeClient, { write: writeContractAsync } as any, {
          address: writeProxy as `0x${string}`,
          abi: abi as Abi,
          functionName: fnName,
          args,
          account: userAddress,
          ...(value !== undefined ? { value } : {}),
        } as any, { enabled: previewWrites });
        if (!res.ok) { setError(res.error || "Preview failed"); return null }
        if (!res.hash) { setError(res.error || "No transaction hash returned"); return null }
        const hash = res.hash as `0x${string}`;
        setLastTx(hash);
        const receipt = await writeClient.waitForTransactionReceipt({ hash });

        // Decode `newListing` from the receipt against the UNION of every facet
        // ABI, not just this call's facet ABI. The event is declared on
        // AuctionManagerSales / English / ERC1155 only, so decoding against a
        // single facet silently returns [] for the others — which is exactly
        // how the listing id goes missing and the flow falls back to guessing.
        // rawLogs are kept so a signature mismatch is visible in the console
        // rather than hidden behind an empty array.
        const unionAbi = ALL_EVENT_ABIS;
        const newListingLogs = decodeLogs(receipt.logs as Log[], unionAbi, "newListing");
        // Decode EVERY AM event, not just newListing. The close/finalize
        // actions each emit a different event (removedListing,
        // listingCancelled, auctionWinner, auctionNoWinner, dutchAuctionEnd)
        // and the backend needs those args.
        const allEvents = decodeAllAuctionEvents(receipt.logs as Log[]);
        let decodedEventId: string | null = null;
        let decodedArgs: any = null;
        if (newListingLogs.length > 0) {
          decodedArgs = (newListingLogs[0] as any).args;
          decodedEventId = decodedArgs?.auctionId != null ? String(decodedArgs.auctionId) : null;
        }
        // DEV-gated on process.env: this repo has no `vite/client` type
        // reference, so `import.meta.env` isn't typed in this file. Vite still
        // replaces process.env.NODE_ENV at build time, so the branch is
        // statically eliminated from production bundles.
        if (typeof process !== "undefined" && process.env?.NODE_ENV !== "production") {
          console.group(`[auction] ${fnName} receipt ${hash}`);
          console.log("status:", receipt.status, "block:", receipt.blockNumber?.toString(), "gasUsed:", receipt.gasUsed?.toString());
          console.log("raw logs:", receipt.logs.length, "newListing decoded:", newListingLogs.length);
          console.log("decoded newListing args:", decodedArgs);
          // Every decoded AM event, one table row each. This is the payload
          // the backend endpoints consume, so print it in the same shape.
          console.table?.(
            allEvents.map((e: any) => ({
              logIndex: e.logIndex,
              eventName: e.eventName,
              emitter: e.address,
              args: e.args
                ? Object.fromEntries(Object.entries(e.args).map(([k, v]) => [k, typeof v === "bigint" ? v.toString() : v]))
                : null,
            }))
          );
          console.log("all decoded events (full):", allEvents);
          // Hand the caller whichever event characterises this action, so the
          // modal does not have to know which event each function emits.
          const terminal =
            findEvent(allEvents, "removedListing") ||
            findEvent(allEvents, "listingCancelled") ||
            findEvent(allEvents, "dutchAuctionEnd") ||
            findEvent(allEvents, "auctionWinner") ||
            findEvent(allEvents, "auctionNoWinner") ||
            findEvent(allEvents, "newListing");
          console.log("terminal event for this action:", terminal);
          if (!newListingLogs.length && receipt.logs.length) {
            console.warn("no newListing match — emitting log topics/signatures:", receipt.logs.map((l: any) => ({ address: l.address, topics: l.topics, data: l.data })));
          }
          console.groupEnd();
        }
        setCreatedAuctionId(decodedEventId);
        setLastReceipt({
          hash, blockNumber: receipt.blockNumber?.toString() || null,
          gasUsed: receipt.gasUsed?.toString() || null,
          status: receipt.status || null,
          logs: receipt.logs.map((l: any) => ({ address: l.address, data: l.data, topics: l.topics })),
          decodedEventId,
          decodedEvents: allEvents,
        });
        return { hash, receipt, decodedEventId, events: allEvents };
      } catch (e: any) {
        setError(e?.message || String(e));
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [walletClient, publicClient, walletChainClient, userAddress, writeContractAsync, allAbis, connectedProxyAddress, chainId, previewWrites]
  );

  // -----------------------------------------------------------------------
  // Chain time — the latest block's timestamp on the READ chain.
  // Countdowns ("ends in") and "listed at" must be measured against chain time,
  // not the browser clock, which can be skewed.
  // -----------------------------------------------------------------------
  const getChainTime = useCallback(async (): Promise<bigint> => {
    if (!publicClient) throw new Error("No public client");
    const block: any = await publicClient.getBlock();
    const ts = block?.timestamp;
    if (ts === undefined || ts === null) throw new Error("Block has no timestamp");
    return BigInt(ts);
  }, [publicClient]);

  // -----------------------------------------------------------------------
  // Diamond storage raw read
  // -----------------------------------------------------------------------
  const readStorageSlot = useCallback(async (key: string) => {
    if (!publicClient) throw new Error("No public client");
    if (!proxyAddress) throw new Error(`No marketplace contract deployed on chain ${readChainId}`);
    const data = await publicClient.getStorageAt({
      address: proxyAddress as `0x${string}`,
      slot: key as `0x${string}`,
    });
    return data;
  }, [publicClient, proxyAddress]);

  // -----------------------------------------------------------------------
  // Convenience wrappers – English auction
  // -----------------------------------------------------------------------
  const createAuction = useCallback(
    (nftContract: string, tokenId: bigint, duration: bigint, reserve: bigint, finalizeOnReserveMet: boolean) =>
      callWrite("createAuction", [nftContract, tokenId, duration, reserve, finalizeOnReserveMet]),
    [callWrite]
  );

  // Dutch (721): createDutchAuction mirrors createAuction's escrow but sets
  // auctionType=DUTCH + seeds dutchCurrentPrice so getCurrentDutchPrice/
  // acceptDutchAuctionPrice operate on 721 tokens.
  const createDutchAuction = useCallback(
    (nftContract: string, tokenId: bigint, startPrice: bigint, reservePrice: bigint, duration: bigint) =>
      callWrite("createDutchAuction", [nftContract, tokenId, startPrice, reservePrice, duration]),
    [callWrite]
  );

  // submitNewBid: the contract requires msg.value == bidAmount + buyerFee and
  // records bid = msg.value - buyerFee. `value` lets a caller pass the
  // fee-inclusive total; it defaults to bidAmount, which is correct when the
  // collection runs under the SELLER model (buyerFee == 0).
  const submitNewBid = useCallback(
    (nftContract: string, tokenId: bigint, bidAmount: bigint, value?: bigint) =>
      callWrite("submitNewBid", [nftContract, tokenId, bidAmount], value ?? bidAmount),
    [callWrite]
  );

  const finalizeAuction = useCallback(
    (nftContract: string, tokenId: bigint) => callWrite("finalizeAuction", [nftContract, tokenId]),
    [callWrite]
  );

  const finalizeEarly = useCallback(
    (nftContract: string, tokenId: bigint) => callWrite("finalizeEarly", [nftContract, tokenId]),
    [callWrite]
  );

  const makeOffer = useCallback(
    (nftContract: string, tokenId: bigint, amount: bigint) =>
      callWrite("makeOffer", [nftContract, tokenId, amount]),
    [callWrite]
  );

  // acceptOffer(uint256 offerId) — single arg on-chain; the old wrapper
  // passed a second `expectedSeller` and failed ABI encoding.
  const acceptOffer = useCallback(
    (offerId: bigint) => callWrite("acceptOffer", [offerId]),
    [callWrite]
  );

  const getActiveAuction = useCallback(
    (auctionID: bigint) => callRead("getActiveAuction", [auctionID]),
    [callRead]
  );

  // NOTE: the contract signature is getAuctionStatus(address nftContract,
  // uint256 tokenId) — it is keyed by TOKEN, not by auction id, and the
  // auctionID comes back in the second return value. The old single-arg
  // wrapper passed the auction id as the address and failed ABI encoding.
  const getAuctionStatus = useCallback(
    (nftContract: string, tokenId: bigint) => callRead("getAuctionStatus", [nftContract, tokenId]),
    [callRead]
  );

  const getAuctionFinancials = useCallback(
    (auctionID: bigint) => callRead("getAuctionFinancials", [auctionID]),
    [callRead]
  );

  const getActiveAuctionIDs = useCallback(() => callRead("getActiveAuctionIDs", []), [callRead]);

  // For-sale listings (721 + 1155): enumerate active listing ids + read details.
  const getActiveSaleListingIDs = useCallback(() => callRead("getActiveSaleListingIDs", []), [callRead]);

  const getSaleListingInfo = useCallback(
    (listingId: bigint) => callRead("getSaleListingInfo", [listingId]),
    [callRead]
  );

  const getBidCount = useCallback(
    (nftContract: string, tokenId: bigint) => callRead("getBidCount", [nftContract, tokenId]),
    [callRead]
  );

  const getTokenSaleHistory = useCallback(
    (nftContract: string, tokenId: bigint) => callRead("getTokenSaleHistory", [nftContract, tokenId]),
    [callRead]
  );

  const getCICount = useCallback(
    (wallet: string, nftContract: string) => callRead("getCICount", [wallet, nftContract]),
    [callRead]
  );

  const getCICountOnly = useCallback(
    (wallet: string, nftContract: string) => callRead("getCICountOnly", [wallet, nftContract]),
    [callRead]
  );

  const minNextBidAmount = useCallback(
    (nftContract: string, tokenId: bigint) => callRead("minNextBidAmount", [nftContract, tokenId]),
    [callRead]
  );

  // -----------------------------------------------------------------------
  // Analytics getters (volume, floor, counts)
  // -----------------------------------------------------------------------
  const getFloorPrice = useCallback(
    (nftContract: string) => callRead("getFloorPrice", [nftContract]),
    [callRead]
  );

  const getTotalVolume = useCallback(
    (nftContract: string) => callRead("getTotalVolume", [nftContract]),
    [callRead]
  );

  const getSaleCount = useCallback(
    (nftContract: string) => callRead("getSaleCount", [nftContract]),
    [callRead]
  );

  const getVolumeByType = useCallback(
    (nftContract: string, vtype: VolumeType) => callRead("getVolumeByType", [nftContract, vtype]),
    [callRead]
  );

  const getCountByType = useCallback(
    (nftContract: string, vtype: VolumeType) => callRead("getCountByType", [nftContract, vtype]),
    [callRead]
  );

  const getGlobalTotalVolume = useCallback(() => callRead("getGlobalTotalVolume", []), [callRead]);

  const getGlobalSaleCount = useCallback(() => callRead("getGlobalSaleCount", []), [callRead]);

  // -----------------------------------------------------------------------
  // Dutch auction
  // -----------------------------------------------------------------------
  const getCurrentDutchPrice = useCallback(
    (auctionID: bigint) => callRead("getCurrentDutchPrice", [auctionID]),
    [callRead]
  );

  // -----------------------------------------------------------------------
  // ERC-1155
  // -----------------------------------------------------------------------
  const createERC1155SaleListing = useCallback(
    (nftContract: string, tokenClassId: bigint, quantity: bigint, reserve: bigint) =>
      callWrite("createERC1155SaleListing", [nftContract, tokenClassId, quantity, reserve]),
    [callWrite]
  );

  // createERC1155Auction takes a quantity between the class id and the duration.
  const createERC1155Auction = useCallback(
    (nftContract: string, tokenClassId: bigint, quantity: bigint, duration: bigint, reserve: bigint, finalizeOnReserveMet: boolean) =>
      callWrite("createERC1155Auction", [nftContract, tokenClassId, quantity, duration, reserve, finalizeOnReserveMet]),
    [callWrite]
  );

  const createERC1155DutchAuction = useCallback(
    (nftContract: string, tokenClassId: bigint, quantity: bigint, startPrice: bigint, reservePrice: bigint, duration: bigint) =>
      callWrite("createERC1155DutchAuction", [nftContract, tokenClassId, quantity, startPrice, reservePrice, duration]),
    [callWrite]
  );

  // batchCreateERC1155Auction takes quantities (parallel to tokenClassIds).
  const batchCreateERC1155Auction = useCallback(
    (nftContracts: string[], tokenClassIds: bigint[], quantities: bigint[], duration: bigint, reserve: bigint, finalizeOnReserveMet: boolean) =>
      callWrite("batchCreateERC1155Auction", [nftContracts, tokenClassIds, quantities, duration, reserve, finalizeOnReserveMet]),
    [callWrite]
  );

  const acceptERC1155SaleListing = useCallback(
    (nftContract: string, tokenClassId: bigint) =>
      callWrite("acceptERC1155SaleListing", [nftContract, tokenClassId]),
    [callWrite]
  );

  const acceptERC1155AuctionPrice = useCallback(
    (nftContract: string, tokenClassId: bigint) =>
      callWrite("acceptERC1155AuctionPrice", [nftContract, tokenClassId]),
    [callWrite]
  );

  const acceptERC1155DutchAuctionPrice = useCallback(
    (nftContract: string, tokenClassId: bigint) => callWrite("acceptERC1155DutchAuctionPrice", [nftContract, tokenClassId]),
    [callWrite]
  );

  const makeERC1155Offer = useCallback(
    (nftContract: string, tokenClassId: bigint, amount: bigint) =>
      callWrite("makeERC1155Offer", [nftContract, tokenClassId, amount]),
    [callWrite]
  );

  const getERC1155SaleListing = useCallback(
    (nftContract: string, tokenClassId: bigint) => callRead("getERC1155SaleListing", [nftContract, tokenClassId]),
    [callRead]
  );

  const getERC1155AuctionStatus = useCallback(
    (nftContract: string, tokenClassId: bigint) => callRead("getERC1155AuctionStatus", [nftContract, tokenClassId]),
    [callRead]
  );

  const getERC1155CurrentDutchPrice = useCallback(
    (auctionID: bigint) => callRead("getERC1155CurrentDutchPrice", [auctionID]),
    [callRead]
  );

  const getERC1155Volume = useCallback(
    (nftContract: string) => callRead("getERC1155Volume", [nftContract]),
    [callRead]
  );

  const minNextBidAmountERC1155 = useCallback(
    (nftContract: string, tokenClassId: bigint) => callRead("minNextBidAmountERC1155", [nftContract, tokenClassId]),
    [callRead]
  );

  // -----------------------------------------------------------------------
  // English — sale listings + ownership helper
  // -----------------------------------------------------------------------
  const createSaleListing = useCallback(
    (nftContract: string, tokenId: bigint, price: bigint) =>
      callWrite("createSaleListing", [nftContract, tokenId, price]),
    [callWrite]
  );

  const acceptSaleListing = useCallback(
    (nftContract: string, tokenId: bigint, value: bigint) =>
      callWrite("acceptSaleListing", [nftContract, tokenId], value),
    [callWrite]
  );

  // Seller pulls their own for-sale listing. No value -- a fixed-price listing
  // holds no escrow, so there is nothing to pay on the way out. (Auction
  // removal is removeListingBeforeEndTime and does take a fee.)
  const cancelSaleListing = useCallback(
    (nftContract: string, tokenId: bigint) =>
      callWrite("cancelSaleListing", [nftContract, tokenId]),
    [callWrite]
  );

  const getOwnerOf = useCallback(
    (nftContract: string, tokenId: bigint) => callRead("getOwnerOf", [nftContract, tokenId]),
    [callRead]
  );

  // Quote for the "pull auction early" confirmation dialog. Returns the exact
  // penalty the seller must send plus the bidder's refund, so the UI never has
  // to hardcode removeListingFeePercentage or recompute the split. Zero
  // penalty is the normal case for an auction nobody bid on.
  const getRemovalFee = useCallback(
    async (nftContract: string, tokenId: bigint) => {
      const r = (await callRead("getRemovalFee", [nftContract, tokenId])) as any[];
      const [penalty, bidderCompensation, fee, refundAmount, hasBidder, isRunning] = r;
      return {
        penalty: BigInt(penalty ?? 0n),
        bidderCompensation: BigInt(bidderCompensation ?? 0n),
        fee: BigInt(fee ?? 0n),
        refundAmount: BigInt(refundAmount ?? 0n),
        hasBidder: Boolean(hasBidder),
        isRunning: Boolean(isRunning),
      };
    },
    [callRead]
  );

  // Quote for "end auction now and sell to the high bidder". Distinct from
  // getRemovalFee: different percentage, and it comes off the seller's
  // proceeds rather than being sent as msg.value -- so the UI must not put a
  // value field on this one. Zero when nobody bid.
  const getEarlyFinalizeFee = useCallback(
    async (nftContract: string, tokenId: bigint) => {
      const r = (await callRead("getEarlyFinalizeFee", [nftContract, tokenId])) as any[];
      const [penalty, bidderReceives, hasBidder, isRunning, reserveMet] = r;
      return {
        penalty: BigInt(penalty ?? 0n),
        bidderReceives: BigInt(bidderReceives ?? 0n),
        hasBidder: Boolean(hasBidder),
        isRunning: Boolean(isRunning),
        reserveMet: Boolean(reserveMet),
      };
    },
    [callRead]
  );

  // All five global percentages in one read. Fetched as a set rather than
  // individually so a page cannot render a mix of pre- and post-reconfigure
  // values if an admin changes fees between two calls.
  const getGlobalFeeConfig = useCallback(async () => {
    const r = (await callRead("getGlobalFeeConfig", [])) as any[];
    const [earlyFinalize, removeListing, salesFee, bidIncrement, treasuryBps] = r;
    return {
      earlyFinalizeFeePercentage: Number(earlyFinalize ?? 0),
      removeListingFeePercentage: Number(removeListing ?? 0),
      salesFeePercentage: Number(salesFee ?? 0),
      nextBidMinPercentage: Number(bidIncrement ?? 0),
      treasuryFeeBps: Number(treasuryBps ?? 0),
    };
  }, [callRead]);

  // Raw per-contract override. feePct === 0 does NOT mean a 0% fee -- it means
  // "no override, fall back to the global salesFeePercentage". hasOverride is
  // the flag to branch on.
  const getContractFeeSettings = useCallback(
    async (nftContract: string) => {
      const r = (await callRead("getContractFeeSettings", [nftContract])) as any[];
      const [feePct, model, hasOverride] = r;
      return {
        feePct: Number(feePct ?? 0),
        // 0 = SPLIT (buyer and seller each pay half), 1 = SELLER (buyer pays 0)
        model: Number(model ?? 0) as FeeModel,
        hasOverride: Boolean(hasOverride),
      };
    },
    [callRead]
  );

  // Resolved per-contract config: what actually gets charged, plus the raw
  // override and the global treasury cut (there is no per-contract treasury
  // config, so treasuryFeeBps here is always the global one).
  const getFeeConfig = useCallback(
    async (nftContract: string) => {
      const r = (await callRead("getFeeConfig", [nftContract])) as any[];
      const [effectiveFeePct, effectiveModel, overrideFeePct, hasOverride, treasuryBps] = r;
      return {
        effectiveFeePct: Number(effectiveFeePct ?? 0),
        effectiveModel: Number(effectiveModel ?? 0) as FeeModel,
        overrideFeePct: Number(overrideFeePct ?? 0),
        hasOverride: Boolean(hasOverride),
        treasuryFeeBps: Number(treasuryBps ?? 0),
      };
    },
    [callRead]
  );

  // -----------------------------------------------------------------------
  // Dutch auction — buyer pays msg.value = current decayed price
  // -----------------------------------------------------------------------
  const acceptDutchAuctionPrice = useCallback(
    (nftContract: string, tokenId: bigint, value: bigint) =>
      callWrite("acceptDutchAuctionPrice", [nftContract, tokenId], value),
    [callWrite]
  );

  // -----------------------------------------------------------------------
  // ERC-1155 finalize — non-reserve settlement (C2 fix)
  // -----------------------------------------------------------------------
  const finalizeERC1155Auction = useCallback(
    (nftContract: string, tokenClassId: bigint) =>
      callWrite("finalizeERC1155Auction", [nftContract, tokenClassId]),
    [callWrite]
  );

  const finalizeERC1155Early = useCallback(
    (nftContract: string, tokenClassId: bigint) =>
      callWrite("finalizeERC1155Early", [nftContract, tokenClassId]),
    [callWrite]
  );

  // -----------------------------------------------------------------------
  // Offers facet — offer lifecycle + shared refund reserve
  // -----------------------------------------------------------------------
  const cancelOffer = useCallback(
    (offerId: bigint) => callWrite("cancelOffer", [offerId]),
    [callWrite]
  );

  // Buyer pays msg.value = offer.amount + buyerFee on finalize.
  const finalizeOffer = useCallback(
    (offerId: bigint, value: bigint) => callWrite("finalizeOffer", [offerId], value),
    [callWrite]
  );

  const claimRefund = useCallback(() => callWrite("claimRefund", []), [callWrite]);

  const refundBalanceOf = useCallback(
    (wallet: string) => callRead("refundBalanceOf", [wallet]),
    [callRead]
  );

  // -----------------------------------------------------------------------
  // Core — admin/treasury/fee config
  // -----------------------------------------------------------------------
  const setGasAccount = useCallback(
    (a: string) => callWrite("setGasAccount", [a]),
    [callWrite]
  );

  const setAdminAddress = useCallback(
    (addr: string, admin: boolean) => callWrite("setAdminAddress", [addr, admin]),
    [callWrite]
  );

  const showFeesAccrued = useCallback(() => callRead("showFeesAccrued", []), [callRead]);

  const configureFeePercentages = useCallback(
    (earlyFinalize: bigint, removeListing: bigint, salesFee: bigint, bidIncrement: bigint, treasuryBps: bigint) =>
      callWrite("configureFeePercentages", [earlyFinalize, removeListing, salesFee, bidIncrement, treasuryBps]),
    [callWrite]
  );

  const setContractFeeConfig = useCallback(
    (nftContract: string, feePct: bigint, model: bigint) =>
      callWrite("setContractFeeConfig", [nftContract, feePct, model]),
    [callWrite]
  );

  const clearContractFeeConfig = useCallback(
    (nftContract: string) => callWrite("clearContractFeeConfig", [nftContract]),
    [callWrite]
  );

  const getContractFeeConfig = useCallback(
    (nftContract: string) => callRead("getContractFeeConfig", [nftContract]),
    [callRead]
  );

  const getEffectiveFeeConfig = useCallback(
    (nftContract: string) => callRead("getEffectiveFeeConfig", [nftContract]),
    [callRead]
  );

  const getHighBid = useCallback(
    (nftContract: string, tokenId: bigint) => callRead("getHighBid", [nftContract, tokenId]),
    [callRead]
  );

  const refundsOwedTotal = useCallback(() => callRead("refundsOwedTotal", []), [callRead]);

  const treasuryTotalOwed = useCallback(() => callRead("treasuryTotalOwed", []), [callRead]);

  // Admin-only; withdraws only held − (refundsOwedTotal + treasuryTotalOwed).
  const withdrawTreasury = useCallback(() => callWrite("withdrawTreasury", []), [callWrite]);

  // -----------------------------------------------------------------------
  // Diamond management
  // -----------------------------------------------------------------------
  const diamondCut = useCallback(
    (cuts: any[], init: string, calldata: string) =>
      callWrite("diamondCut", [cuts, init as any, calldata as any]),
    [callWrite]
  );

  const diamondCutLength = useCallback(() => callRead("diamondCutLength", []), [callRead]);

  const getFacets = useCallback(() => callRead("getFacets", []), [callRead]);

  const getFunctionSelector = useCallback(
    (facetAddr: string) => callRead("getFunctionSelector", [facetAddr]),
    [callRead]
  );

  const facet = useCallback(
    (index: bigint) => callRead("facet", [index]),
    [callRead]
  );

  // -----------------------------------------------------------------------
  // Core
  // -----------------------------------------------------------------------
  const initialize = useCallback(
    () => callWrite("initialize", []),
    [callWrite]
  );

  const owner = useCallback(() => callRead("owner", []), [callRead]);

  const paused = useCallback(() => callRead("paused", []), [callRead]);

  const transferOwnership = useCallback(
    (newOwner: string) => callWrite("transferOwnership", [newOwner]),
    [callWrite]
  );

  // -----------------------------------------------------------------------
  // Auto-expose every FN_ROUTES entry as a bound function (read/write derived from ABI)
  // -----------------------------------------------------------------------
  const autoFns = useMemo(() => {
    const out: Record<string, (...args: any[]) => Promise<any>> = {};
    for (const fnName of Object.keys(FN_ROUTES)) {
      const facet = FN_ROUTES[fnName];
      const abi = allAbis[facet] || [];
      const fnDef = abi.find((i: any) => i.type === 'function' && i.name === fnName);
      const isView = fnDef && (fnDef.stateMutability === 'view' || fnDef.stateMutability === 'pure');
      // Payable functions: when the caller passes more positional args than the
      // ABI declares (e.g. acceptSaleListing(nft, tokenId, PRICE)), the overflow
      // is the msg.value, not a function param. Without this split, viem throws
      // "ABI encoding params/values length mismatch" (3 values to a 2-param fn)
      // and the price is never attached as payment.
      out[fnName] = (...raw: any[]) => {
        const isPayable = fnDef && fnDef.stateMutability === 'payable';
        let args = raw;
        let value;
        if (isPayable && fnDef && raw.length > fnDef.inputs.length) {
          value = raw[raw.length - 1];
          args = raw.slice(0, fnDef.inputs.length);
        }
        return isView ? callRead(fnName, args) : callWrite(fnName, args, value);
      };
    }
    return out;
  }, [allAbis, callRead, callWrite]);

  // -----------------------------------------------------------------------
  // Expose for component use
  // -----------------------------------------------------------------------
  const publicClientRef = useRef(publicClient);
  publicClientRef.current = publicClient;
  const walletClientRef = useRef(walletClient);
  walletClientRef.current = walletClient;

  const approveNFT = useCallback(
    async (nftContract: string, tokenId: bigint, approveTo?: string): Promise<`0x${string}`> => {
      if (!walletClient || !userAddress) throw new Error("Wallet not connected");
      const toAddr = (approveTo || connectedProxyAddress) as `0x${string}`;
      if (!toAddr) throw new Error(`No marketplace contract deployed on chain ${chainId}`);
      const res = await previewWrite(walletChainClient || publicClient, { write: writeContractAsync } as any, {
        address: nftContract as `0x${string}`,
        abi: [{
          name: "approve",
          type: "function",
          inputs: [{ name: "to", type: "address" }, { name: "tokenId", type: "uint256" }],
          outputs: [],
          stateMutability: "nonpayable",
        }] as Abi,
        functionName: "approve",
        args: [toAddr, tokenId],
        account: userAddress,
      } as any, { enabled: previewWrites })
      if (!res.ok) throw new Error(res.error || 'Preview failed')
      if (!res.hash) throw new Error(res.error || 'No transaction hash returned')
      const hash = res.hash as `0x${string}`;
      return hash;
    },
    [walletClient, userAddress, writeContractAsync, connectedProxyAddress, walletChainClient, publicClient]
  );

  // ERC-1155 approval-for-all: grants the diamond proxy operator right over
  // every class the sender holds (the 1155 equivalent of per-token approve).
  const approveNFTForAll = useCallback(
    async (nftContract: string, approved: boolean): Promise<`0x${string}`> => {
      if (!walletClient || !userAddress) throw new Error("Wallet not connected");
      const res = await previewWrite(publicClient, { write: writeContractAsync } as any, {
        address: nftContract as `0x${string}`,
        abi: [{
          name: "setApprovalForAll",
          type: "function",
          inputs: [{ name: "operator", type: "address" }, { name: "approved", type: "bool" }],
          outputs: [],
          stateMutability: "nonpayable",
        }] as Abi,
        functionName: "setApprovalForAll",
        args: [proxyAddress as `0x${string}`, approved],
        account: userAddress,
      } as any, { enabled: previewWrites })
      if (!res.ok) throw new Error(res.error || 'Preview failed')
      const hash = res.hash
      return hash;
    },
    [walletClient, userAddress, writeContractAsync, proxyAddress]
  );

  // Detect the token standard from the NFT contract: ERC-1155 exposes
  // balanceOf(address,uint256); ERC-721 here only has single-arg balanceOf
  // (or none), which reverts. Returns "1155" | "721", or null if unknowable.
  const detectTokenStandard = useCallback(
    async (nftContract: string, tokenId: bigint): Promise<"1155" | "721" | null> => {
      if (!publicClient) return null;
      try {
        await publicClient.readContract({
          address: nftContract as `0x${string}`,
          abi: [{
            name: "balanceOf",
            type: "function",
            inputs: [{ name: "account", type: "address" }, { name: "id", type: "uint256" }],
            outputs: [{ name: "", type: "uint256" }],
            stateMutability: "view",
          }],
          functionName: "balanceOf",
          args: [userAddress as `0x${string}`, tokenId],
        } as any);
        return "1155";
      } catch {
        return "721";
      }
    },
    [publicClient, userAddress]
  );

  // -----------------------------------------------------------------------
    // Return — memoized so consumers don't get a new object every render
    // -----------------------------------------------------------------------
    return useMemo(() => ({
      // State
      loading,
      isTxLoading,
      error,
      result,
      lastTx,
      createdAuctionId,
      lastReceipt,
      proxyAddress,
      storageSlot: STORAGE_SLOT,
      chainId,
      userAddress,
      publicClient,
      // The CONNECTED chain's public client. Callers MUST use this (not
      // `publicClient`) to wait for receipts of transactions they sent:
      // `publicClient` tracks the READ chain, which is a different chain
      // whenever opts.chainId points at a collection elsewhere, and waiting on
      // the wrong chain hangs forever with no error.
      walletChainClient,
      walletClient,
      // Generic
      callRead,
      callWrite,
      callWriteWithReceipt,
      readStorageSlot,
    getChainTime,
      // Approval
      approveNFT,
      approveNFTForAll,
      detectTokenStandard,

      // English auction
      createAuction,
      createDutchAuction,
      createSaleListing,
      acceptSaleListing,
      cancelSaleListing,
      submitNewBid,
      finalizeAuction,
      finalizeEarly,
      makeOffer,
      acceptOffer,
      getOwnerOf,
      getRemovalFee,
      getEarlyFinalizeFee,
      getGlobalFeeConfig,
      getContractFeeSettings,
      getFeeConfig,
      getActiveAuction,
      getAuctionStatus,
      getAuctionFinancials,
      getActiveAuctionIDs,
      getActiveSaleListingIDs,
      getSaleListingInfo,
      getBidCount,
      getTokenSaleHistory,
      getCICount,
      getCICountOnly,
      minNextBidAmount,

      // Analytics (English facet)
      getFloorPrice,
      getTotalVolume,
      getSaleCount,
      getVolumeByType,
      getCountByType,
      getGlobalTotalVolume,
      getGlobalSaleCount,

      // Dutch auction
      getCurrentDutchPrice,
      acceptDutchAuctionPrice,

      // ERC-1155
      createERC1155SaleListing,
      createERC1155Auction,
      createERC1155DutchAuction,
      batchCreateERC1155Auction,
      acceptERC1155SaleListing,
      acceptERC1155AuctionPrice,
      acceptERC1155DutchAuctionPrice,
      makeERC1155Offer,
      finalizeERC1155Auction,
      finalizeERC1155Early,
      getERC1155SaleListing,
      getERC1155AuctionStatus,
      getERC1155CurrentDutchPrice,
      getERC1155Volume,
      minNextBidAmountERC1155,

      // Offers facet
      cancelOffer,
      finalizeOffer,
      claimRefund,
      refundBalanceOf,

      // Core — admin / treasury / fees
      setGasAccount,
      setAdminAddress,
      showFeesAccrued,
      configureFeePercentages,
      setContractFeeConfig,
      clearContractFeeConfig,
      getContractFeeConfig,
      getEffectiveFeeConfig,
      getHighBid,
      refundsOwedTotal,
      treasuryTotalOwed,
      withdrawTreasury,

      // Diamond management
      diamondCut,
      diamondCutLength,
      getFacets,
      getFunctionSelector,
      facet,

      // Core
      initialize,
      owner,
      paused,
      transferOwnership,

      // Auto-bound methods (all FN_ROUTES entries)
      ...autoFns,

      // All function routes
      routes: FN_ROUTES,

      // Volume types (for type-safe enum usage in frontend)
      VolumeType: {
        SALE_LISTING: 0 as VolumeType,
        AUCTION_ENGLISH: 1 as VolumeType,
        AUCTION_DUTCH: 2 as VolumeType,
        OFFER: 3 as VolumeType,
        ERC1155: 4 as VolumeType,
      },
    }), [loading, isTxLoading, error, result, lastTx, createdAuctionId, lastReceipt, chainId, userAddress, publicClient, walletChainClient, walletClient, callWriteWithReceipt, callRead, callWrite, readStorageSlot, getChainTime, approveNFT, approveNFTForAll, detectTokenStandard, createAuction, createDutchAuction, createSaleListing, acceptSaleListing, cancelSaleListing, submitNewBid, finalizeAuction, finalizeEarly, makeOffer, acceptOffer, getOwnerOf, getRemovalFee, getEarlyFinalizeFee, getGlobalFeeConfig, getContractFeeSettings, getFeeConfig, getActiveAuction, getAuctionStatus, getAuctionFinancials, getActiveAuctionIDs, getActiveSaleListingIDs, getSaleListingInfo, getBidCount, getTokenSaleHistory, getCICount, getCICountOnly, minNextBidAmount, getFloorPrice, getTotalVolume, getSaleCount, getVolumeByType, getCountByType, getGlobalTotalVolume, getGlobalSaleCount, getCurrentDutchPrice, acceptDutchAuctionPrice, createERC1155SaleListing, createERC1155Auction, createERC1155DutchAuction, batchCreateERC1155Auction, acceptERC1155SaleListing, acceptERC1155AuctionPrice, acceptERC1155DutchAuctionPrice, makeERC1155Offer, finalizeERC1155Auction, finalizeERC1155Early, getERC1155SaleListing, getERC1155AuctionStatus, getERC1155CurrentDutchPrice, getERC1155Volume, minNextBidAmountERC1155, cancelOffer, finalizeOffer, claimRefund, refundBalanceOf, setGasAccount, setAdminAddress, showFeesAccrued, configureFeePercentages, setContractFeeConfig, clearContractFeeConfig, getContractFeeConfig, getEffectiveFeeConfig, getHighBid, refundsOwedTotal, treasuryTotalOwed, withdrawTreasury, diamondCut, diamondCutLength, getFacets, getFunctionSelector, facet, initialize, owner, paused, transferOwnership, decodeEventLogs, autoFns]);
}

export default useAuctionManager;